import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { findIndex } from 'lodash';

import { config } from '@config/pendo.config.json';
import { AngularContext } from 'react-app';
import t from 'react-translate';

import { cloneDeepSerializable, useAppDispatch } from 'redux/store';
import { Submission } from 'redux/schemas/models/exercise';
import { RatingActivityExercise } from 'redux/schemas/models/exercise-skills-rating';
import { setSubmission } from 'redux/actions/submissions';
import { deleteFeedback, resetSkillsFeedbackState } from 'redux/actions/skills-feedback';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getCurrentUserId } from 'redux/selectors/users';

import { NvResponsiveTabsDisplayType, NvTab } from 'shared/components/nv-responsive-tabs';
import NvResponsiveTabsRow from 'shared/components/nv-responsive-tabs-row';
import { gray5, warning } from 'styles/global_defaults/colors';
import { confettiZIndex, halfSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';

import { RootState } from 'redux/schemas';
import { Tabs, buildSkillRatingTabs } from '../skills-rating/skills-rating-tabs';
import YourFeedbackTab from '../skills-rating/tabs/your-feedback-tab';
import AllFeedbacksTab from '../skills-rating/tabs/all-feedbacks-tab';
import UserFeedbackTab from '../skills-rating/tabs/user-feedback-tab';
import AiInsightsPanel from './ai-insights-panel';

const AI_INSIGHTS_TAB = 'ai_insights';

const tabDataQa = config.pendo.submissionView.rightColumn.tab;

const styles = css`
  display: flex;
  flex-direction: column;
  height: 100%;

  .tabs-bar {
    position: relative;
    z-index: ${confettiZIndex + 2};
    background: linear-gradient(-45deg, rgba(220, 253, 253, 0.3) 0%, rgba(247, 251, 232, 0.3) 100%);
    border-bottom: 1px solid ${gray5};
    padding: ${threeQuartersSpacing}px 0 0;

    .nv-responsive-tabs-row-wrapper {
      justify-content: center;

      > div:first-child {
        margin-left: 0;
      }
    }

    /* The right column is narrow, so the default tab spacing pushes the labels
    apart and forces them to wrap. Tighten it and keep each label on one line. */
    .desktop-tabs .tab {
      padding-left: ${halfSpacing}px;
      padding-right: ${halfSpacing}px;

      &:not(:last-of-type) {
        margin-right: ${halfSpacing}px;
      }

      .tab-text {
        white-space: nowrap;
      }
    }
  }

  .tabs-content {
    flex: 1;
    overflow-y: auto;
  }

  .all-feedbacks-wrapper {
    padding: ${standardSpacing}px;
  }
`;

const CombinedTabs: React.FC = () => {
  const { $scope, injectServices } = useContext(AngularContext);
  const [$state, $timeout, CurrentPermissionsManager] = injectServices([
    '$state',
    '$timeout',
    'CurrentPermissionsManager',
  ]);
  const { reportId, selected, userId, isRatingFeedback } = $state?.params ?? {};
  const dispatch = useAppDispatch();

  const submission: Submission = useSelector((state: RootState) => state.models.submissions?.[reportId]);
  const exercise = submission?.exercise as RatingActivityExercise;
  const currentInstitutionId = useSelector((state: RootState) => state.app.currentInstitutionId);
  const currentUserId = useSelector((state: RootState) => getCurrentUserId(state));

  const [activeTab, setActiveTab] = useState<string>(AI_INSIGHTS_TAB);
  const panelRef = useRef<HTMLDivElement>(null);
  const isInitialTab = useRef(true);

  const isOwnSubmission = submission?.isCollaborator;
  const isAdmin = CurrentPermissionsManager.isTeachingAssistant()
    || CurrentPermissionsManager.isInstructor()
    || submission?.isUserMentorOfCollaborators;

  const tabs = useMemo(() => {
    const list: (NvTab & { key?: string })[] = [{
      text: t.SUBMISSIONS.AI_ASSIGNMENT.TITLE_INSIGHT(),
      onClick: () => setActiveTab(AI_INSIGHTS_TAB),
      key: AI_INSIGHTS_TAB,
      dataQA: `${tabDataQa}-${AI_INSIGHTS_TAB}`,
    }];

    const skillTabs = buildSkillRatingTabs({
      isOwnSubmission,
      submission,
      userId,
      isAdmin,
      setActiveTab,
    }).map(tab => ({ ...tab, dataQA: `${tabDataQa}-${tab.key}` }));

    return [...list, ...skillTabs];
  }, [isOwnSubmission, submission, userId, isAdmin]);

  useEffect(() => () => {
    dispatch(resetSkillsFeedbackState({ ownerType: 'Report', ownerId: reportId }));
  }, [dispatch, reportId]);

  useEffect(() => {
    if (selected && selected !== 'comments') {
      setActiveTab(selected);
    } else if (isRatingFeedback && submission?.isRatedByCurrentUser) {
      setActiveTab(Tabs.SKILLS_FEEDBACK);
    } else if (userId) {
      setActiveTab(Tabs.USER_FEEDBACK);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRatingFeedback, selected, submission?.isRatedByCurrentUser, userId]);

  useEffect(() => {
    if ($scope.ReportsManager?.currentReport) {
      const { currentReport } = $scope.ReportsManager;
      currentReport.submitting = currentReport.submittingObject;
      dispatch(setSubmission(cloneDeepSerializable(currentReport)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, $scope.ReportsManager?.currentReport]);

  // Move focus to the freshly revealed panel when switching tabs, but never on
  // first render so opening the submission view doesn't steal focus.
  useEffect(() => {
    if (isInitialTab.current) {
      isInitialTab.current = false;
      return;
    }
    panelRef.current?.focus({ preventScroll: true });
  }, [activeTab]);

  const onDeleteFeedback = (deletedByUserId?: number) => {
    const warningTitle = deletedByUserId
      ? t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.ASSIGNMENT.SKILLS_FEEDBACK.SUBMISSION.DELETE.ADMIN.TITLE()
      : t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.ASSIGNMENT.SKILLS_FEEDBACK.SUBMISSION.DELETE.AUTHOR();
    const desc = deletedByUserId
      ? t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.ASSIGNMENT.SKILLS_FEEDBACK.SUBMISSION.DELETE.ADMIN.DESC()
      : '';

    dispatch(openConfirmationDialog({
      onConfirm: () => dispatch(deleteFeedback({
        institutionId: currentInstitutionId,
        ownerId: reportId,
        ownerType: 'Report',
        userId: deletedByUserId ?? currentUserId,
        feedbackCriteriaId: exercise.skillsRatingFeedback.id,
      })).then((data) => {
        $timeout(() => { $scope.toggleRatingForm(false); });
        if ($scope.ReportsManager?.currentReport) {
          $scope.ReportsManager.currentReport.isRatedByCurrentUser = false;

          const { requiredToComplete, numReviewesCompleted } = data.payload?.skillsRating ?? {};
          if ($scope.ReportsManager.currentReport?.exercise?.skillsRatingFeedback) {
            $scope.ReportsManager.currentReport.exercise.skillsRatingFeedback = {
              ...$scope.ReportsManager.currentReport.exercise.skillsRatingFeedback,
              requiredToComplete,
              numReviewesCompleted,
            };
          }
        }
      }),
      cancelText: t.FORM.CANCEL(),
      confirmText: t.FORM.YES_SURE(),
      title: warningTitle,
      bodyText: desc,
    }));
  };

  if (!submission) return null;

  const activeIndex = findIndex(tabs, tab => tab.key === activeTab);
  const activeTabLabel = tabs.find(tab => tab.key === activeTab)?.text;

  return (
    <div css={styles}>
      <div className='tabs-bar'>
        <NvResponsiveTabsRow
          defaultTabs={tabs}
          tabTextClass='card-title'
          tabType={NvResponsiveTabsDisplayType.TEXT_ONLY}
          activeBorderColor={warning}
          revertActiveTab={activeIndex >= 0 ? activeIndex : 0}
          containerResponsive
        />
      </div>
      <div
        className='tabs-content'
        role='tabpanel'
        aria-label={activeTabLabel}
        tabIndex={-1}
        ref={panelRef}
      >
        {activeTab === AI_INSIGHTS_TAB && <AiInsightsPanel />}
        {activeTab === Tabs.SKILLS_FEEDBACK && (
          <YourFeedbackTab reportId={reportId} onDeleteFeedback={onDeleteFeedback} />
        )}
        {activeTab === Tabs.USER_FEEDBACK && (
          <UserFeedbackTab reportId={reportId} feedbackBy={userId} />
        )}
        {activeTab === Tabs.ALL_FEEDBACK && (
          <div className='all-feedbacks-wrapper'>
            <AllFeedbacksTab
              reportId={reportId}
              isAdmin={isAdmin}
              onDeleteFeedback={onDeleteFeedback}
              feedbackCriteriaId={exercise?.skillsRatingFeedback?.id}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CombinedTabs;
